import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { makeTranslationsSchema } from '../../common/translations/translations';
import { ViolationActionSchema } from '../violation-action/violation-action';
import { ViolationSeveritySchema } from '../violation-severity/violation-severity';

/**
 * The stable slug staff never change once messages have been judged by it.
 *
 * Same grammar as {@link AiClassificationLabelSchema}'s `key`, and that is not a
 * coincidence: **the key IS the classification label** handed to the model, so a
 * key this file accepted and the AI platform rejected would be a violation type
 * that silently never matches anything.
 */
export const VIOLATION_TYPE_KEY_PATTERN = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/;

export const ViolationTypeKeySchema = z
  .string()
  .min(2)
  .max(64)
  .regex(VIOLATION_TYPE_KEY_PATTERN, 'A violation type key is lower_snake_case.');

/**
 * What the model is told this category means — the entire definition it works
 * from.
 *
 * Bounded by `AiClassificationLabelSchema.description`'s own ceiling, because
 * this string is copied into it verbatim. Staff retune the classifier by editing
 * this text, which is the whole reason `moderation.ai.*` has no prompt setting:
 * the prompt is the catalogue.
 */
export const CLASSIFIER_GUIDANCE_MAX_LENGTH = 2000;

/** Per-locale content (N5, W7) — what a member is told this violation was. */
export const ViolationTypeTranslationSchema = z
  .object({
    /** Short label: "Sexual content". Rendered in the notice and in the queue. */
    name: z.string().trim().min(1).max(120),
    /** One or two sentences a member reads on the notice. */
    description: z.string().trim().min(1).max(1000),
  })
  .strict();

export type ViolationTypeTranslation = z.infer<typeof ViolationTypeTranslationSchema>;

export const ViolationTypeTranslationsSchema = makeTranslationsSchema(
  ViolationTypeTranslationSchema,
);

/**
 * One staff-authored category of violation, with the consequence it carries.
 *
 * **This row is the configuration N20 means.** `EnforcementService` reads it at
 * the moment it acts, never a copy taken at classification time, which is what
 * makes "changing a type's action in admin changes behaviour without deploy"
 * true rather than aspirational.
 *
 * The locale-resolved `name`/`description` are merged on by the repository
 * (`findLocalized`); the admin edit form reads {@link ViolationTypeDetailSchema}
 * instead, which carries every locale (W7).
 */
export const ViolationTypeSchema = z
  .object({
    id: UuidSchema,
    key: ViolationTypeKeySchema,
    severity: ViolationSeveritySchema,
    action: ViolationActionSchema,
    /**
     * How long a `suspend` lasts. Null for every other action, and **required**
     * for `suspend` — an indefinite suspension is a ban wearing the wrong word,
     * and the elapse sweep could never lift it.
     */
    suspensionDays: z.number().int().min(1).max(3650).nullable(),
    /**
     * Act only after a human has looked, however confident the classifier was.
     *
     * The third route into `queued_for_review`, and the one an operator controls
     * per type rather than globally: a `critical` category may well be worth
     * enforcing automatically while a subtler one is worth reading first.
     */
    reviewBeforeEnforce: z.boolean(),
    /**
     * The notification template rendered as this type's own notice (N17).
     *
     * The two standing notices — legal consequences and no-refund-on-ban — are
     * platform-wide constants added by `EnforcementService` on top of this one,
     * never re-declared per type, so an operator cannot create a type whose
     * notice omits them.
     */
    legalNoticeTemplateKey: z.string().trim().min(1).max(120),
    classifierGuidance: z.string().trim().min(1).max(CLASSIFIER_GUIDANCE_MAX_LENGTH),
    /**
     * Inactive types are **not** deleted and **not** classified against.
     *
     * Deactivating rather than deleting is the same discipline the permission
     * catalogue keeps (N8: orphans deactivated, never deleted) and for the same
     * reason — `violations` rows reference this row for ever, and a
     * reviewer reading a two-year-old case must still be able to see what it was
     * judged under.
     */
    isActive: z.boolean(),
    name: z.string(),
    description: z.string(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .strict();

export type ViolationType = z.infer<typeof ViolationTypeSchema>;

/**
 * A type as the **admin edit form** reads it: every locale, not just the
 * caller's (W7).
 *
 * The resolved `name`/`description` stay on the shape so the same row can be
 * rendered in a list without a second request.
 */
export const ViolationTypeDetailSchema = ViolationTypeSchema.extend({
  translations: ViolationTypeTranslationsSchema,
}).strict();

export type ViolationTypeDetail = z.infer<typeof ViolationTypeDetailSchema>;
